import styled from "styled-components";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MDBCard,
  MDBCardHeader,
  MDBCardTitle,
  MDBCardBody,
} from "mdb-react-ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCheckCircle,
  faStar,
  faTimes,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Button, Row, Col } from "../App.styles";
import { Alert, Spinner } from "../common";
import { AcceptModal, RejectModal } from "./modals";
import {
  useAcceptMutation,
  useRejectMutation,
  useFeatureMutation,
} from "./Monitor.state";
import AnswerMedia from "./AnswerMedia";
import Timeago from "../common/Timeago";
import { useSetRecoilState } from "recoil";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import MapDisplay from "../common/MapDisplay";
import { AnswerRowWrapper } from "./AnswerComments.style";
import { useRegenerateMutation } from './Answer.state'
import { useDecision } from '@optimizely/react-sdk'
import AnswerActions from './AnswerActions'
import ChallengeDescription from "../organize/ChallengeDescription";
import { ActionSpace } from "./AnswerActions.style";
import AnswerAdjustments from "./AnswerAdjustments";

const Card = styled(MDBCard)`
  margin: 0;
  z-index: 2;

  + .card {
    margin-top: 1rem;
  }
`;

const CardBody = styled(MDBCardBody)`
  height: 910px;

  svg {
    margin-left: 0.625rem;
  }
`;

const AnswerCol = styled(Col)(({ theme }) => `
  flex-direction: column;
  align-items: stretch;

  > div {
    margin-right: 0;
  }

  > .alert {
    margin-bottom: 0;
  }

  @media (max-width: ${theme.maxMobileWidth}) {
    font-size: 0.875rem;
    margin-top: 1rem;

    a {
      color: ${theme.primaryColor};
    }

    p {
      margin-right: 0;
    }
  }

  ${theme.breakpoints.up("sm")} {
    .nubs__item > * {
      max-width: none;
      width: 100%;
    }
  }
`);

const AnswerMediaProcessing = styled.div`
  background-color: #f5f5f5;
  padding: 8rem 4rem;
  flex-grow: 1;
  text-align: center;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonCol = styled(Col)`
  ${({ theme }) => `
    margin-top: 12px;

    ${theme.breakpoints.down('md')} {
      ${Button} {
        font-size: 0.875rem;
        line-height: 1;
        margin-right: 2px;
        padding: 0.5rem 1rem;

        span {
          display: none;
        }
      }
    }

    ${theme.breakpoints.up('sm')} {
      justify-content: flex-end;
    }

    ${theme.breakpoints.up('lg')} {
      margin-top: 0;

      ${Button} {
        svg {
          display: none;
        }
      }
    }
  `}
`;

const MediaCol = styled(Col)`
  justify-content: center;
  align-items: flex-start;
  height: 384px;

  > div {
    margin-right: 0;
  }
`;

const RegenerateButton = styled(Button)`
  font-size: 1rem;
  font-weight: normal;
  padding: 0;
  text-transform: none;

  &:hover,
  &:active,
  &:focus {
    background-color: transparent;
  }
`;

export const UpdatedAt = styled.abbr`
  cursor: help;
  margin-left: 0.625rem;
`;

export const P = styled.p`
  margin-top: 1.5rem;

  iframe {
    max-width: 100%;
  }
`;

export const AnswerMeta = styled.div`
    display: flex;
    margin-top: 12px;
    justify-content: space-between;
    align-items: center;
`

const isProcessing = (type, subtype, photo, video, sticker) => {
  switch (type) {
    case 1:
    case 7:
    case 9:
      return !photo || !photo.url;
    case 2:
    case 8:
    case 10:
      if (subtype === 2) {
        return false;
      }

      return !video || (!video.urls.mp4 && !video.urls.webm && !video.urls.gif);
    case 3:
      return !sticker || !sticker.url;
    default:
      return false;
  }
};

const Answer = (props) => {
  const {
    id,
    onLoad,
    assets,
    type,
    sub_type,
    duplicate,
    created_at,
    likes,
    approved_at,
    featured_at,
    rejected_at,
    comment,
    game,
    late,
    latitude,
    longitude,
    points,
    regenerate,
    review_key,
    share_location,
    task,
    team,
    trivia,
    streamType,
  } = props;
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const { game_id } = useParams();
  const { accept } = useAcceptMutation(id, review_key);
  const { reject } = useRejectMutation(id, review_key);
  const { feature } = useFeatureMutation(id, review_key);
  const { mutateAsync: doRegenerate, isLoading: isRegenerating } = useRegenerateMutation();
  const setAlert = useSetRecoilState(alertState);
  const [decisionMediaVersions] = useDecision('media-versions')

  const onAccept = async (points, message) => {
    setStatus("");
    await accept({ message, points });
  };

  const onReject = async (points, message) => {
    setStatus("");
    await reject({ message, points });
  };

  const onFeature = async () => {
    setStatus("");
    await feature();
  };

  const onRegenerate = async () => {
    try {
        await doRegenerate(id)
        setAlert({
            type: 'info',
            message: t("Regeneration of media has been queued for this submission."),
        });
    } catch (e) {
        let errorMessage = ''
        switch (e?.response?.status) {
            case 404:
                errorMessage = t(`The answer is not found.`);
                break;
            case 406:
                errorMessage = t(`The media cannot be regenerated for this submission.`);
                break;
            default:
                errorMessage = e.toJSON()?.message;
                break;
        }
        setAlert({
            type: 'error',
            message: errorMessage,
        })
    } finally {
        window.dispatchEvent(new CustomEvent('scroll'))
    }
  };

  const audio = assets?.audios?.find((audio) => audio.name === "standard");
  const photo =
    type === 9
      ? assets?.photos?.find(
          (photo) => photo.name === "background-merged" && photo.url
        )
      : assets?.photos?.find((photo) => photo.name === "standard" && photo.url);
  const sticker = assets?.photos?.find(
    (photo) => photo.name === "background-sticker" && photo.url
  );
  const video =
    type === 10
      ? assets?.videos?.find((video) => video.name === "background-merged")
      : assets?.videos?.find((video) => video.name === "standard");
  const processing = isProcessing(type, sub_type, photo, video, sticker);
  const markerPath = useMemo(() => {
    const isTeam = streamType === 'team'
    const imgId = isTeam ? team?.id : id
    const imgPrefix = isTeam ? 'team' : 'answer'
    return `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/pins/generated/${imgPrefix}_${imgId}.png`
  }, [id, team?.id, streamType])

  const SummaryLine = () => (
    <div className="mt-4 mb-4">
        {team.id ? (
        <Link to={`/teams/stream/${game.id}/${team.id}`}>
            {team.name}
        </Link>
        ) : (
        t("Anonymous")
        )}
        {game.name && (
        <>
            {t(" played ")}
            &nbsp;
            {task && (
            <Link to={`/tasks/stream/${game.id}/${task.id}`}>
                {task.name}
            </Link>
            )}
            &nbsp;
            {t(" challenge in ")}
            &nbsp;
            <Link to={`/games/stream/${game.id}`}>{game.name}</Link>
            &nbsp;
        </>
        )}
        {points === 1 ? t(" for 1 point.") : `${t(' for')} ${points} ${t('points')}.`}
    </div>
  )

  return task ? (
    <>
      <Card>
        <MDBCardHeader>
          <MDBCardTitle>
            <Row>
              <Col sm="4" lg="6">{task?.name}</Col>
              <ButtonCol sm="8" lg="6">
                <Button
                  color={approved_at ? "dark" : "light"}
                  onClick={() => setStatus("accept")}
                >
                  <span>{rejected_at ? t("Approve") : t("Update")}</span>
                  <FontAwesomeIcon icon={faCheck} />
                </Button>
                <Button
                  color={rejected_at ? "dark" : "light"}
                  onClick={() => setStatus("reject")}
                >
                  <span>{t('Reject')}</span>
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
                <Button
                  color={featured_at ? "dark" : "light"}
                  disabled={!!featured_at}
                  onClick={onFeature}
                >
                  <span>{t('Feature')}</span>
                  <FontAwesomeIcon icon={faStar} />
                </Button>
              </ButtonCol>
            </Row>
          </MDBCardTitle>
        </MDBCardHeader>
        <CardBody>
          <Row>
            <Col size="12" lg="6">
                <Row className="flex-grow-1">
                    <MediaCol size="12">
                        {processing ? (
                            <AnswerMediaProcessing>
                            {t('Media is processing')}
                            </AnswerMediaProcessing>
                        ) : (
                            <AnswerMedia
                            onLoad={onLoad}
                            audio={audio}
                            photo={photo}
                            video={video}
                            ratio={photo?.width / photo?.height}
                            />
                        )}
                    </MediaCol>
                </Row>
            </Col>
            <Col size="12" lg="6">
              {!!(latitude) && share_location !== "hide" && (
                <MapDisplay
                    mapContainerStyle={{ width: "100%", height: "100%", minHeight: "100px" }}
                    center={{ lat: latitude, lng: longitude }}
                    defaultZoom={15}
                    onLoad={onLoad}
                    withMarker={true}
                    markerPath={markerPath}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col size="12" lg="6" className="col--stretch col--align-start">
                <AnswerRowWrapper>
                    <Row>
                        <AnswerCol size="12">
                        {comment && (
                            <Alert color="info">
                            <span>
                                {type === 4
                                ? t("QR Code scanned successfully.")
                                : `"${comment}"`}
                                {trivia === "correct" && (
                                <FontAwesomeIcon icon={faCheckCircle} />
                                )}
                                {trivia === "incorrect" && (
                                <FontAwesomeIcon icon={faTimesCircle} />
                                )}
                            </span>
                            </Alert>
                        )}
                        <SummaryLine />
                        {task && <ChallengeDescription id={task.id} preview={task.description} />}
                        </AnswerCol>
                    </Row>
                </AnswerRowWrapper>
            </Col>
            <AnswerCol size="12" lg="6">
              <div>
                {
                    decisionMediaVersions?.enabled
                        ? (
                            <AnswerActions 
                                {...props} 
                                photo={photo?.url}
                                sticker={sticker?.url}
                                video={video}
                                audio={audio?.url}
                                regenerateState={{
                                    regenerate,
                                    isRegenerating,
                                    onRegenerate,
                                }}
                            />
                        )
                        : (
                            <>
                                <AnswerMeta>
                                    <span>{likes} likes</span>
                                    <ActionSpace direction="horizontal">
                                        <Timeago.Display time={created_at} />
                                    </ActionSpace>
                                </AnswerMeta>
                                {late === 2 && (
                                <span className="late text-danger">{t('Late Submission!')}</span>
                                )}
                                {late === 3 && (
                                <span className="late text-danger">{t('Early Submission!')}</span>
                                )}
                                <br />
                                <Link
                                title={t("Share directly via this URL.")}
                                to={`/answers/show/${game_id}/${id}`}
                                >
                                url
                                </Link>{" "}
                                {!!duplicate && "(duplicate)"}
                                {regenerate && (
                                <>
                                    <br />
                                    <RegenerateButton
                                    color="link"
                                    onClick={onRegenerate}
                                    title={t("Attempt to regenerate the media for this answer.")}
                                    >
                                    {t('Regenerate')} { isRegenerating ? <Spinner size="0.875rem" /> : null }
                                    </RegenerateButton>
                                </>
                                )}
                                <br />
                                {audio?.url && (
                                <a
                                    href={audio.url}
                                    className="tools"
                                    title={t("Download this video.")}
                                >
                                    {t('Download')}
                                </a>
                                )}
                                {parseInt(type) === 2 && parseInt(sub_type) === 1 ? (
                                <a
                                    href={video.urls.gif}
                                    className="tools"
                                    title={t("Download this video.")}
                                >
                                    {t('Download')}
                                </a>
                                ) : (
                                video?.urls.mp4 && (
                                    <a
                                    href={video.urls.mp4}
                                    className="tools"
                                    title={t("Download this video.")}
                                    >
                                    {t('Download')}
                                    </a>
                                )
                                )}
                                {sticker?.url && (
                                <>
                                    <a
                                    href={sticker.url}
                                    className="tools"
                                    title={t("Download this sticker.")}
                                    >
                                    {t('Sticker')}
                                    </a>
                                </>
                                )}
                            </>
                        )
                }
              </div>
            </AnswerCol>
          </Row>
        </CardBody>
      </Card>
      <AnswerAdjustments id={id} />
      <AcceptModal
        show={status === "accept"}
        onConfirm={onAccept}
        onDismiss={() => setStatus("")}
        currentPoints={points}
        defaultPoints={task?.points}
      />
      <RejectModal
        show={status === "reject"}
        onConfirm={onReject}
        onDismiss={() => setStatus("")}
      />
    </>
  ) : null;
};

export default Answer;
